import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { journeyAPI } from '@/services/api';
import { CheckCircle, Clock, Briefcase, ChevronRight, XCircle, Trophy } from 'lucide-react';

const STAGE_FALLBACK = {
  matched: 'Matched',
  screened: 'Resume Matched',
  ai_screening: 'AI Screening Round',
  decision: 'Decision',
  documents: 'Documents Required',
  offer_released: 'Offer Letter Released',
  offer_signed: 'Signed Offer Letter',
};

export default function ApplicantJourney() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const d = await journeyAPI.listMine();
        setApps(d.applications || []);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load journey');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <p className="text-slate-500 dark:text-zinc-500">Loading journey...</p>;

  return (
    <div data-testid="applicant-journey">
      <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">My Journey</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">Track every application end-to-end. Click any card for the full stage timeline, recruiter notes, and offer documents.</p>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300 px-4 py-2 rounded-lg mb-4 text-sm">{error}</div>}

      {apps.length === 0 ? (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-10 text-center">
          <Briefcase className="w-10 h-10 text-slate-600 dark:text-zinc-700 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-zinc-500">No applications yet</p>
          <p className="text-slate-500 dark:text-zinc-600 text-xs mt-1">Once you&apos;re matched to a job, your journey will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {apps.map(a => (
            <button key={a.match_id}
              onClick={() => navigate(`/applicant/journey/${a.match_id}`)}
              data-testid={`journey-card-${a.match_id}`}
              className="block w-full text-left bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 hover:border-slate-300 dark:hover:border-zinc-700 transition-colors rounded-xl p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-slate-900 dark:text-white font-medium truncate">{a.job_title}</p>
                  <p className="text-slate-500 dark:text-zinc-500 text-xs mt-0.5">
                    Match {Math.round(a.match_percentage || 0)}% · {new Date(a.created_at).toLocaleDateString()}
                  </p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <StageBadge stage={a.current_stage} label={a.stage_label || STAGE_FALLBACK[a.current_stage] || a.current_stage} outcome={a.final_outcome} />
                    <OutcomePill outcome={a.final_outcome} />
                    {a.job_status === 'closed' && <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-500">role closed</span>}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-500 dark:text-zinc-600 mt-1 shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StageBadge({ stage, label, outcome }) {
  if (outcome === 'rejected') {
    return <span className="text-[11px] px-2 py-0.5 rounded bg-red-500/10 text-red-700 dark:text-red-300 flex items-center gap-1"><XCircle className="w-3 h-3" /> Ended at {label}</span>;
  }
  if (outcome === 'selected') {
    return <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 flex items-center gap-1"><Trophy className="w-3 h-3" /> {label}</span>;
  }
  return <span className="text-[11px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300 flex items-center gap-1"><Clock className="w-3 h-3" /> {label}</span>;
}

function OutcomePill({ outcome }) {
  if (outcome === 'selected') return <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Selected</span>;
  if (outcome === 'rejected') return <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-red-500/15 text-red-700 dark:text-red-300">Rejected</span>;
  return <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400">In progress</span>;
}
